import React, { useEffect, useState } from 'react';
import { Layout, Button, theme, Menu, Dropdown, Avatar, Modal, Spin, notification, Typography } from 'antd';
import { LoadingOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, ProfileOutlined, SettingOutlined } from '@ant-design/icons';
import { baseurl } from '../lib/settings';
import EditionSelectionModal from '../modal/EditionSelectionModal';
import { useEdition } from '../../components/context/EditionContext';

const { Text } = Typography;

function Navbar({ collapsed, setCollapsed }) {
  const { Header } = Layout;
  const [userName, setUserName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [userNameLoading, setUserNameLoading] = useState(true);
  const [editions, setEditions] = useState([]);
  const [error, setError] = useState(null);
  const [modalVisibleEdition, setModalVisibleEdition] = useState(true);
  const [api, contextHolder] = notification.useNotification();
  const { selectedEditionUp, updateEditionUp, refreshTrigger } = useEdition();

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const fetchName = () => {
    setUserNameLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && user.user_name) {
        setUserName(user.user_name);
      } else {
        setUserName("Nome não disponível");
      }
    } catch (error) {
      setUserName("Erro ao carregar nome");
    } finally {
      setUserNameLoading(false);
    }
  };

  const fetchEditions = () => {
    baseurl.get('v3/get_available_editions/by_company/')
      .then((response) => {
        const editionsData = response.data;
        setEditions(editionsData);

        // 1. Verifica se já tem uma edição selecionada no localStorage
        const savedEdition = localStorage.getItem('selectedEdition');
        if (savedEdition) {
          const parsedEdition = JSON.parse(savedEdition);
          updateEditionUp(parsedEdition);
          setModalVisibleEdition(false);
          return;
        }

        // 2. Se não tiver, busca a edição ativa (is_active: true)
        const activeEdition = editionsData.find(edition => edition.is_active);
        if (activeEdition) {
          updateEditionUp(activeEdition);
          setModalVisibleEdition(false);
          api.info({
            message: 'Edição ativa selecionada',
            description: `${activeEdition.description} foi selecionada automaticamente.`,
          });
        } else {
          // 3. Se não encontrar edição ativa, mantém o modal aberto para seleção manual
          setModalVisibleEdition(true);
        }
      })
      .catch((error) => {
        setError(error);
        console.error("Error fetching editions:", error);
      });
  };

  useEffect(() => {
    fetchEditions();
    fetchName();

    const handleStorageChange = (event) => {
      if (event.key === 'user') {
        fetchName();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleEditionSelected = (editionId) => {
    const edition = editions.find(e => e.id === editionId);
    if (edition) {
      updateEditionUp(edition);
      api.success({
        message: edition.description,
        description: `Edição selecionada com sucesso!`,
      });
      setModalVisibleEdition(false);
    }
  };

  const doLogout = async () => {
    setModalVisible(true);
    setIsLoading(true);
    try {
      await baseurl.post("auth/logout-session/");
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("selectedEdition");
      window.location.replace("/");
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLoading(false);
      setModalVisible(false);
    }
  };

  const menuOptions = (
    <Menu>
      <Menu.Item disabled key="1" icon={<ProfileOutlined />}>Perfil</Menu.Item>
      <Menu.Item disabled key="2" icon={<SettingOutlined />}>Definições</Menu.Item>
      <Menu.Item key="3" icon={<LogoutOutlined />} onClick={doLogout}>Sair</Menu.Item>
    </Menu>
  );

  const getInitials = (name) => {
    const names = name.split(' ');
    return names.length > 1
      ? `${names[0][0]}${names[names.length - 1][0]}`
      : `${names[0][0]}`;
  };

  return (
    <>
      {contextHolder}
      <Header
        style={{
          padding: 0,
          background: colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Left side group - Collapse button and Edition name */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />

          <div style={{ marginLeft: 16 }}>
            {selectedEditionUp ? (
              <Button
                type="dashed" danger
                // FIXME descomentar para selecionar edição no Navbar
                disabled
                onClick={() => setModalVisibleEdition(true)}
                style={{
                  padding: '0 15px',
                  height: 30,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Text strong style={{ fontSize: 16 }}>
                  {selectedEditionUp.description} - {selectedEditionUp.created_at}
                </Text>
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={() => setModalVisibleEdition(true)}
                style={{ height: 64, display: 'flex', alignItems: 'center' }}
              >
                Selecionar Edição
              </Button>
            )}
          </div>
        </div>

        {/* Right side - User info */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {userNameLoading ? (
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              style={{ marginRight: 24 }}
            />
          ) : (
            <>
              <Text style={{ marginRight: 8, fontSize: 16 }}>{userName}</Text>
              <Dropdown overlay={menuOptions} trigger={['hover']}>
                <Avatar
                  style={{
                    backgroundColor: '#040350',
                    cursor: 'pointer',
                    marginRight: 24,
                  }}
                >
                  {getInitials(userName)}
                </Avatar>
              </Dropdown>
            </>
          )}
        </div>
      </Header>

      <EditionSelectionModal
        visible={modalVisibleEdition}
        onClose={() => selectedEditionUp && setModalVisibleEdition(false)}
        onEditionSelected={handleEditionSelected}
      />

      <Modal
        title="Terminando sessão"
        open={modalVisible}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
      >
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
          tip="Por favor aguarde..."
          size="large"
        />
      </Modal>
    </>
  );
}

export default Navbar;